import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Button,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import Edit_Email_FundSubscriber_By_Admin from '../Provider Permissions Detail Dialog/Edit Email Fund Subscriber By Admin';

const AdminFundSubscriberTable = ({ subscribers, loading, onUpdate }) => {
  const [selectedAddresses, setSelectedAddresses] = useState({});
  const [updateCount, setUpdateCount] = useState(0);
  
  // Trigger parent refresh when updateCount changes
  useEffect(() => {
    if (updateCount > 0 && onUpdate) {
      onUpdate();
    }
  }, [updateCount, onUpdate]);

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return {
        date: format(date, 'MMM dd, yyyy'),
        time: format(date, 'HH:mm:ss') + ' ET (UTC-05:00)'
      };
    } catch (error) {
      return { date: 'N/A', time: 'N/A' };
    }
  };

  // Format address to show full address with ellipsis for very long addresses
  const formatAddress = (address) => {
    if (!address) return 'N/A';
    if (typeof address === 'string') {
      // Show full address, but limit display length if too long
      if (address.length > 42) {
        return `${address.slice(0, 10)}...${address.slice(-10)}`;
      }
      return address;
    }
    return String(address);
  };

  // Get addresses for a subscriber
  const getAddresses = (subscriber) => {
    if (!subscriber.walletAddress) return [];
    if (Array.isArray(subscriber.walletAddress)) {
      return subscriber.walletAddress;
    }
    return [subscriber.walletAddress];
  };

  // Get selected address for a subscriber
  const getSelectedAddress = (subscriber) => {
    const addresses = getAddresses(subscriber);
    if (addresses.length === 0) return 'N/A';

    const subscriberId = subscriber._id || subscriber.id;
    const selectedIndex = selectedAddresses[subscriberId] || 0;
    return addresses[selectedIndex] || addresses[0];
  };

  // Handle address selection change
  const handleAddressChange = (subscriberId, addressIndex) => {
    setSelectedAddresses(prev => ({
      ...prev,
      [subscriberId]: addressIndex
    }));
  };

  // Format total investment
  const formatTotalInvestment = (ethAmount, usdAmount) => {
    const eth = ethAmount ? parseFloat(ethAmount) : 0;
    const usd = usdAmount ? parseFloat(usdAmount) : 0;
    
    return {
      eth: `${eth.toFixed(5)} ETH`,
      usd: `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`
    };
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px',
          backgroundColor: 'white'
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ marginLeft: '16px', color: '#666' }}>
          Loading fund subscribers...
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ backgroundColor: 'white' }}>
      <Table sx={{ minWidth: 1600, width: '100%' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#000000' }}>
            {/* Joined Fund on */}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: '180px',
                padding: '16px 12px',
                border: 'none'
              }}
            >
              Joined Fund on
            </TableCell>
            
            {/* Last Transaction */}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: '180px',
                padding: '16px 12px',
                border: 'none'
              }}
            >
              Last Transaction
            </TableCell>
            
            {/* Name */}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: '120px',
                padding: '16px 12px',
                border: 'none'
              }}
            >
              Name
            </TableCell>
            
            {/* Address */}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: '150px',
                padding: '16px 12px',
                border: 'none'
              }}
            >
              Address
            </TableCell>
            
            {/* Email - Moved after Address */}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: '200px',
                padding: '16px 12px',
                border: 'none'
              }}
            >
              Email
            </TableCell>
            
            {/* Status */}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: '100px',
                padding: '16px 12px',
                border: 'none'
              }}
            >
              Status
            </TableCell>
            
            {/* Transactions */}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: '150px',
                padding: '16px 12px',
                border: 'none'
              }}
            >
              Transactions
            </TableCell>
            
            {/* Total Investment */}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: '200px',
                padding: '16px 12px',
                border: 'none'
              }}
            >
              Total Investment
            </TableCell>
            
            {/* Edit Email */}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: '120px',
                padding: '16px 12px',
                border: 'none'
              }}
            >
              Edit Email
            </TableCell>
            
            {/* Transaction */}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: '150px',
                padding: '16px 12px',
                border: 'none'
              }}
            >
              Transaction
            </TableCell>
          </TableRow>
        </TableHead>
        
        <TableBody>
          {subscribers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} sx={{ textAlign: 'center', padding: '40px' }}>
                <Typography variant="body1" color="textSecondary">
                  No fund subscribers found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            subscribers.map((subscriber, index) => {
              const joinedDate = formatDateTime(subscriber.joinedDate || subscriber.createdAt);
              const lastTransactionDate = formatDateTime(subscriber.lastTransactionDate);
              const totalInvestment = formatTotalInvestment(
                subscriber.totalInvestmentETH || subscriber.totalInvestment?.eth,
                subscriber.totalInvestmentUSD || subscriber.totalInvestment?.usd
              );

              return (
                <TableRow
                  key={subscriber._id || index}
                  sx={{
                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                    '&:hover': { backgroundColor: '#f0f8ff' }
                  }}
                >
                  {/* Joined Fund on */}
                  <TableCell sx={{ padding: '12px' }}>
                    <Box>
                      <Typography variant="body2" fontSize="13px" fontWeight="500">
                        {joinedDate.date}
                      </Typography>
                      <Typography variant="body2" fontSize="12px" color="#666">
                        {joinedDate.time}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  {/* Last Transaction */}
                  <TableCell sx={{ padding: '12px' }}>
                    <Box>
                      <Typography variant="body2" fontSize="13px" fontWeight="500">
                        {lastTransactionDate.date}
                      </Typography>
                      <Typography variant="body2" fontSize="12px" color="#666">
                        {lastTransactionDate.time}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  {/* Name */}
                  <TableCell sx={{ padding: '12px' }}>
                    <Typography variant="body2" fontWeight="500" fontSize="13px">
                      {subscriber.name || 
                       (subscriber.firstName ? `${subscriber.firstName}${subscriber.lastName ? ' ' + subscriber.lastName : ''}`.trim() : '') ||
                       subscriber.fullName || 
                       'N/A'}
                    </Typography>
                  </TableCell>
                  
                  {/* Address */}
                  <TableCell sx={{ padding: '12px' }}>
                    {(() => {
                      const addresses = getAddresses(subscriber);
                      const subscriberId = subscriber._id || subscriber.id;

                      if (addresses.length === 0) {
                        return (
                          <Typography variant="body2" fontSize="13px" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            N/A
                          </Typography>
                        );
                      }

                      if (addresses.length === 1) {
                        return (
                          <Typography 
                            variant="body2" 
                            fontSize="13px" 
                            sx={{ 
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                              maxWidth: '200px'
                            }}
                            title={addresses[0]}
                          >
                            {addresses[0]}
                          </Typography>
                        );
                      }

                      return (
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <Select
                            value={selectedAddresses[subscriberId] || 0}
                            onChange={(e) => handleAddressChange(subscriberId, e.target.value)}
                            sx={{
                              fontSize: '13px',
                              fontFamily: 'monospace',
                              '& .MuiSelect-select': {
                                padding: '4px 8px'
                              }
                            }}
                          >
                            {addresses.map((address, index) => (
                              <MenuItem key={index} value={index}>
                                <Typography 
                                  variant="body2" 
                                  fontSize="13px" 
                                  sx={{ 
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all',
                                    maxWidth: '300px'
                                  }}
                                  title={address}
                                >
                                  {address}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    })()}
                  </TableCell>
                  
                  {/* Email - Moved after Address */}
                  <TableCell sx={{ padding: '12px' }}>
                    <Typography variant="body2" fontSize="13px">
                      {subscriber.email || 'N/A'}
                    </Typography>
                  </TableCell>
                  
                  {/* Status */}
                  <TableCell sx={{ padding: '12px' }}>
                    <Chip
                      label={subscriber.status || 'Active'}
                      color={getStatusColor(subscriber.status)}
                      size="small"
                      sx={{
                        fontSize: '11px',
                        height: '24px',
                        backgroundColor: '#d4edda',
                        color: '#155724',
                        '& .MuiChip-icon': {
                          color: '#155724'
                        }
                      }}
                    />
                  </TableCell>
                  
                  {/* Transactions */}
                  <TableCell sx={{ padding: '12px' }}>
                    <Typography variant="body2" fontWeight="600" fontSize="13px">
                      {subscriber.transactionsCount || subscriber.unitsPurchased || '0'}
                    </Typography>
                  </TableCell>
                  
                  {/* Total Investment */}
                  <TableCell sx={{ padding: '12px' }}>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        fontSize="13px"
                        sx={{
                          lineHeight: 1.2,
                          color: '#333',
                          marginBottom: '2px'
                        }}
                      >
                        {totalInvestment.eth}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        fontSize="13px"
                        sx={{
                          lineHeight: 1.2,
                          color: '#666'
                        }}
                      >
                        {totalInvestment.usd}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  {/* Edit Email */}
                  <TableCell sx={{ padding: '12px' }}>
                    <Edit_Email_FundSubscriber_By_Admin
                      fundSubscriberEmail={subscriber.email}
                      setUpdateCount={setUpdateCount}
                      updateCount={updateCount}
                      id={subscriber._id}
                    />
                  </TableCell>
                  
                  {/* Transaction */}
                  <TableCell sx={{ padding: '12px' }}>
                    <Link
                      to={`/admin-panel/FundSubscriberTransactions/${subscriber._id}`}
                      style={{
                        color: '#1976d2',
                        textDecoration: 'none',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      View Details
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AdminFundSubscriberTable;
